import type { ApolloCache } from '@apollo/client';
import { formatDuration, intervalToDuration } from 'date-fns';
import type { DocumentMetadataImportHistoryFieldsFragment } from 'src/gql/generated/apollo-types';
import { DocumentMetadataImportStatus } from 'src/gql/generated/apollo-types';
import * as Apollo from 'src/gql/generated/apollo-types';

const STARTED_ACTIVE_IMPORT_STATUSES = new Set<DocumentMetadataImportStatus>([
  DocumentMetadataImportStatus.Uploaded,
  DocumentMetadataImportStatus.Validating,
]);

/** No progress updates for this long while processing is treated as failed in the UI. */
const STALE_IMPORT_TIMEOUT_MS = 10 * 60 * 1000;

/** After every row is processed, treat lingering Processing as complete if no update arrives. */
const STALE_FULL_PROGRESS_MS = 30 * 1000;

/**
 * Mirrors the server org mutex: Processing rows are active unless stale, while
 * Uploaded/Validating only count once the server has committed a startedAt.
 */
export function getIsCommittedActiveImport(
  importRecord: Pick<
    DocumentMetadataImportHistoryFieldsFragment,
    'failedReason' | 'startedAt' | 'status' | 'summary' | 'updatedAt'
  >
): boolean {
  const displayStatus = getDisplayImportStatus(importRecord);
  if (displayStatus === DocumentMetadataImportStatus.Processing) return true;

  return Boolean(importRecord.startedAt) && STARTED_ACTIVE_IMPORT_STATUSES.has(displayStatus);
}

/** Client-only interim state while the confirm mutation's optimistic row is in flight. */
export function getIsImportConfirmPending(importRecord: {
  status: DocumentMetadataImportStatus;
  startedAt?: string | null;
}): boolean {
  return importRecord.status === DocumentMetadataImportStatus.Validating && !importRecord.startedAt;
}

/** True when the history grid would show the "Loading..." status chip for this import. */
export function getIsImportLoadingDisplay(
  importRecord: Pick<
    DocumentMetadataImportHistoryFieldsFragment,
    'failedReason' | 'startedAt' | 'status' | 'summary' | 'updatedAt'
  >
): boolean {
  return (
    getDisplayImportStatus(importRecord) === DocumentMetadataImportStatus.Validating &&
    getIsCommittedActiveImport(importRecord)
  );
}

export function getDeletedImportsMessage(deletedCount: number): string {
  if (deletedCount === 1) return '1 data migration deleted.';

  return `${deletedCount} data migrations deleted.`;
}

/**
 * Render-time status heuristic. The one liveness gap the server cannot cover is
 * a Temporal worker dying mid-run, which leaves a row Processing forever; after
 * the stale timeout the grid renders it as Failed. Nothing is written back to
 * the cache — the server record stays authoritative.
 */
export function getDisplayImportStatus(
  importRecord: Pick<
    DocumentMetadataImportHistoryFieldsFragment,
    'startedAt' | 'status' | 'summary' | 'updatedAt'
  > & {
    finishedAt?: DocumentMetadataImportHistoryFieldsFragment['finishedAt'];
  }
): DocumentMetadataImportStatus {
  if (getIsStaleProcessingImport(importRecord)) {
    return DocumentMetadataImportStatus.Failed;
  }

  if (importRecord.status === DocumentMetadataImportStatus.Canceled) {
    return DocumentMetadataImportStatus.Failed;
  }

  if (importRecord.status === DocumentMetadataImportStatus.Processing) {
    if (importRecord.finishedAt) {
      return inferCompletedStatusFromSummary(importRecord.summary);
    }

    if (getIsStaleFullyProcessedImport(importRecord)) {
      return inferCompletedStatusFromSummary(importRecord.summary);
    }
  }

  return importRecord.status;
}

export function getDisplayImportFailedReason(
  importRecord: Pick<
    DocumentMetadataImportHistoryFieldsFragment,
    'failedReason' | 'startedAt' | 'status' | 'summary' | 'updatedAt'
  >
): string | null {
  if (getIsStaleProcessingImport(importRecord)) {
    return 'Import timed out or stopped responding.';
  }

  return importRecord.failedReason ?? null;
}

export function getImportEtaLabel(
  importRecord: Pick<
    DocumentMetadataImportHistoryFieldsFragment,
    'startedAt' | 'status' | 'summary' | 'updatedAt'
  > & {
    finishedAt?: DocumentMetadataImportHistoryFieldsFragment['finishedAt'];
  }
): string | null {
  if (getDisplayImportStatus(importRecord) !== DocumentMetadataImportStatus.Processing) return null;
  if (!importRecord.startedAt) return null;

  const totalRows = importRecord.summary?.totalRows ?? 0;
  const processedRowCount = importRecord.summary?.processedRowCount ?? 0;
  if (totalRows <= 0 || processedRowCount <= 0) return null;

  const elapsedMs = Date.now() - new Date(importRecord.startedAt).getTime();
  if (elapsedMs <= 0) return null;

  const remainingRows = Math.max(totalRows - processedRowCount, 0);
  if (remainingRows === 0) return null;

  const msPerRow = elapsedMs / processedRowCount;
  const remainingMs = msPerRow * remainingRows;

  return formatRemainingDuration(remainingMs);
}

/**
 * Inserts a newly created import at the top of the cached history list (sorted by
 * createdAt desc). Field updates on existing records flow through entity
 * normalization automatically, so this only handles list membership.
 */
export function addImportToHistoryList(
  cache: ApolloCache,
  importRecord: DocumentMetadataImportHistoryFieldsFragment
): void {
  const existing = cache.readQuery<Apollo.DocumentMetadataImportsQuery>({
    query: Apollo.DocumentMetadataImportsDocument,
  });
  if (!existing) return;
  if (existing.documentMetadataImports.some(row => row.id === importRecord.id)) return;

  cache.writeQuery<Apollo.DocumentMetadataImportsQuery>({
    data: {
      ...existing,
      documentMetadataImports: [importRecord, ...existing.documentMetadataImports],
    },
    query: Apollo.DocumentMetadataImportsDocument,
  });
}

type ImportSummaryLike =
  | {
      errorRows?: number | null;
      processedRowCount?: number | null;
      totalRows?: number | null;
    }
  | null
  | undefined;

function getIsStaleProcessingImport(importRecord: {
  status: DocumentMetadataImportStatus;
  updatedAt: string;
  startedAt?: string | null;
  summary?: ImportSummaryLike;
}): boolean {
  if (importRecord.status !== DocumentMetadataImportStatus.Processing) return false;
  if (!importRecord.startedAt) return false;
  if (getIsFullyProcessedImport(importRecord)) return false;

  const lastUpdatedMs = new Date(importRecord.updatedAt).getTime();
  return Date.now() - lastUpdatedMs > STALE_IMPORT_TIMEOUT_MS;
}

function getIsFullyProcessedImport(importRecord: { summary?: ImportSummaryLike }): boolean {
  const totalRows = importRecord.summary?.totalRows ?? 0;
  const processedRowCount = importRecord.summary?.processedRowCount ?? 0;

  return totalRows > 0 && processedRowCount >= totalRows;
}

function getIsStaleFullyProcessedImport(importRecord: {
  status: DocumentMetadataImportStatus;
  updatedAt: string;
  summary?: ImportSummaryLike;
}): boolean {
  if (importRecord.status !== DocumentMetadataImportStatus.Processing) return false;
  if (!getIsFullyProcessedImport(importRecord)) return false;

  return Date.now() - new Date(importRecord.updatedAt).getTime() > STALE_FULL_PROGRESS_MS;
}

function inferCompletedStatusFromSummary(summary: ImportSummaryLike): DocumentMetadataImportStatus {
  if ((summary?.errorRows ?? 0) > 0) {
    return DocumentMetadataImportStatus.CompletedWithErrors;
  }

  return DocumentMetadataImportStatus.Completed;
}

function formatRemainingDuration(remainingMs: number): string | null {
  const duration = intervalToDuration({ end: remainingMs, start: 0 });
  const dimensions = [
    { unit: 'hours', value: duration.hours },
    { unit: 'minutes', value: duration.minutes },
    { unit: 'seconds', value: duration.seconds },
  ] satisfies { unit: 'hours' | 'minutes' | 'seconds'; value: number | undefined }[];
  const highestDimension = dimensions.find(dim => Boolean(dim.value)) ?? {
    unit: 'seconds',
    value: 0,
  };

  const formatted = formatDuration({ [highestDimension.unit]: highestDimension.value });
  return formatted ? `~${formatted} left` : null;
}
